import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FC, ReactNode, createContext, useContext, useState } from 'react';
import { Folder } from 'types';

import { useTranslate } from 'hooks/useTranslate';
import { useGetDocuements } from 'modules/documents/hooks/useGetDocuments';
import { setModal } from 'modules/_shared/redux/modalActions';
import { documentsListInitValue } from '../constants/documentsListInitValue';
import { DocumentsListContextType } from '../types/DocumentsListContextType';
import { UploadDocumentModalProvider } from '../../Modals/UploadDocumentModal/context/UploadDocumentModalContext';
import UploadDocumentModal from '../../Modals/UploadDocumentModal';

const DocumentsListContext = createContext<DocumentsListContextType>(
  documentsListInitValue
);

export const DocumentsListProvider: FC<{
  children: ReactNode;
  section: string;
  currentFolder?: Folder;
}> = ({ children, section, currentFolder }) => {
  const { t } = useTranslate();
  const { societyId } = useParams();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const documents = useSelector((state: State) => state.documents || []);

  const [page, setPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [refetchKey, setRefetchKey] = useState(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useGetDocuements({
    societyId,
    folderId: currentFolder?._id,
    page,
    itemsPerPage,
    refetchKey,
  });

  const invalidateDocuments = () => {
    setRefetchKey((v) => v + 1);
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeItemsPerPage = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1);
  };

  const handleAddDocument = () => {
    store.dispatch(
      setModal(
        <UploadDocumentModalProvider
          folder={currentFolder}
          onUploadDocument={invalidateDocuments}
        >
          <UploadDocumentModal />
        </UploadDocumentModalProvider>
      )
    );
  };

  return (
    <DocumentsListContext.Provider
      value={{
        data: {
          totalDocuments: currentFolder?.documentsCount.total || 0,
          documents:
            documents?.filter((doc) => doc.folder === currentFolder?._id) || [],
          section,
          currentFolderId: currentFolder?._id,
          isAdmin: true,
          isReadOnly: false,
          isLoading,
        },
        actions: {
          handleChangePage,
          handleChangeItemsPerPage,
          handleAddDocument,
          handleOpenDocument: () => {},
          handleDeleteDocument: () => {},
        },
      }}
    >
      {children}
    </DocumentsListContext.Provider>
  );
};

export const useDocumentsList = () => {
  const context = useContext(DocumentsListContext);
  if (!context) {
    throw new Error(
      'useDocumentsList must be used within a DocumentsListProvider'
    );
  }
  return context;
};
